import { getFormValues, change, SubmissionError, reset } from "redux-form";
import {
  createActionTypes,
  createAsyncAction,
  createAsyncActionTypes,
  createPayloadAction,
} from "@dpdgroupuk/redux-action-creator";
import * as customerService from "../customer/customer.service";
import * as customerSettingsService from "./customerSettings.service";

import {
  CUSTOMER_SETTINGS,
  CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM,
  CUSTOMER_SETTINGS_CHAT_QUEUE_FORM,
  CUSTOMER_SETTINGS_SEARCH_FORM,
} from "../../constants/forms";
import {
  ACCOUNT_SETTINGS_UPDATED_SUCCESSFULLY,
  ARE_YOU_SURE_BULK_CHAT_QUEUE,
  ARE_YOU_SURE_BULK_CHAT_QUEUE_CSV,
  BULK_CHAT_QUEUE_APPLIED,
  BULK_CHAT_QUEUE_CSV_INVALID_FILE,
  BULK_CHAT_QUEUE_CSV_NO_ACCOUNTS,
  BULK_CHAT_QUEUE_CSV_READ_ERROR,
  BULK_CHAT_QUEUE_ERROR,
  CHAT_QUEUE_REMOVED_SUCCESSFULLY,
  CHAT_QUEUE_UPDATE_ERROR,
  CHAT_QUEUE_UPDATED_SUCCESSFULLY,
  NO,
  YES,
} from "../../constants/strings";
import { ignoreCatch } from "../../utils/async";
import { formatMessage } from "../../utils/string";
import { DPD_BUSINESS_ID } from "../userDetailsForm/accountDetails/accountDetails.constants";
import { createLoadingAction } from "../loader/loader.actions";
import { showInfoModal, showModal } from "../modal/modal.actions";
import {
  customerCustomerSettingsFromForms,
  groupBulkChatQueueItems,
  isCsvFile,
  parseBulkAccountNumbers,
  parseBulkChatQueueCsv,
} from "./customerSettings.models";
import {
  ACCOUNT_FIELD,
  BULK_ACCOUNTS_FIELD,
  BUSINESS_ID_FIELD,
  CHAT_QUEUE_FORM_ERRORS,
  CUSTOMER_SETTINGS_ERRORS,
  MAX_BULK_CHAT_QUEUE_ACCOUNTS,
  QUEUE_TYPE_FIELD,
  SPECIAL_SERVICE_EXCLUSIONS_FIELD,
} from "./customerSettings.constants";

const ACTION_NAMESPACE = "CUSTOMER_SETTINGS";

export const ACTIONS = createActionTypes(ACTION_NAMESPACE, {
  SEARCH_ACCOUNT: createAsyncActionTypes("SEARCH_ACCOUNT"),
  FETCH_CUSTOMER: createAsyncActionTypes("FETCH_CUSTOMER"),
  LOAD_ACCOUNT_SETTINGS: createAsyncActionTypes("LOAD_ACCOUNT_SETTINGS"),
  FETCH_ACCOUNT_SETTINGS: createAsyncActionTypes("FETCH_ACCOUNT_SETTINGS"),
  SAVE_CUSTOMER_SETTINGS: createAsyncActionTypes("SAVE_CUSTOMER_SETTINGS"),
  FETCH_CHAT_QUEUE: createAsyncActionTypes("FETCH_CHAT_QUEUE"),
  LOAD_CHAT_QUEUE: createAsyncActionTypes("LOAD_CHAT_QUEUE"),
  SAVE_CHAT_QUEUE: createAsyncActionTypes("SAVE_CHAT_QUEUE"),
  BULK_SAVE_CHAT_QUEUE: createAsyncActionTypes("BULK_SAVE_CHAT_QUEUE"),
  FETCH_CHAT_QUEUE_ACCOUNTS: createAsyncActionTypes(
    "FETCH_CHAT_QUEUE_ACCOUNTS"
  ),
  REFRESH_CHAT_QUEUE_ACCOUNTS: "REFRESH_CHAT_QUEUE_ACCOUNTS",
  SET_CHAT_QUEUE_ACCOUNT_FILTER: "SET_CHAT_QUEUE_ACCOUNT_FILTER",
});

const refreshChatQueueAccounts = () =>
  createPayloadAction(ACTIONS.REFRESH_CHAT_QUEUE_ACCOUNTS);

const setChatQueueAccountFilter = (account) =>
  createPayloadAction(ACTIONS.SET_CHAT_QUEUE_ACCOUNT_FILTER, account || "");

export const clearAccountField = () => (dispatch) => {
  dispatch(change(CUSTOMER_SETTINGS_SEARCH_FORM, ACCOUNT_FIELD, ""));
  dispatch(setChatQueueAccountFilter(""));
};

export const resetCustomerSettingsForm = () => (dispatch) => {
  dispatch(reset(CUSTOMER_SETTINGS));
  dispatch(reset(CUSTOMER_SETTINGS_CHAT_QUEUE_FORM));
  dispatch(setChatQueueAccountFilter(""));
};

export const fetchAccountSettings = createAsyncAction(
  customerSettingsService.getCustomerSettings,
  ACTIONS.FETCH_ACCOUNT_SETTINGS
);

const fetchCustomer = createAsyncAction(
  customerService.getCustomerByAccountNumber,
  ACTIONS.FETCH_CUSTOMER
);

export const onSearchAccount = createAsyncAction(
  createLoadingAction(() => async (dispatch, getState) => {
    const values = getFormValues(CUSTOMER_SETTINGS_SEARCH_FORM)(getState());
    const account = await dispatch(
      fetchCustomer(values[ACCOUNT_FIELD], values[BUSINESS_ID_FIELD])
    );

    if (!account) {
      throw new SubmissionError({
        [ACCOUNT_FIELD]: CUSTOMER_SETTINGS_ERRORS.ACCOUNT_WAS_NOT_FOUND,
      });
    }

    if (account.business !== values.businessId) {
      const message =
        values.businessId === DPD_BUSINESS_ID
          ? CUSTOMER_SETTINGS_ERRORS.ACCOUNT_WAS_NOT_FOUND_FOR_DPD
          : CUSTOMER_SETTINGS_ERRORS.ACCOUNT_WAS_NOT_FOUND_FOR_DPD_LOCAL;

      throw new SubmissionError({
        [ACCOUNT_FIELD]: message,
      });
    }

    dispatch(setChatQueueAccountFilter(values[ACCOUNT_FIELD]));
  }),
  ACTIONS.SEARCH_ACCOUNT
);

export const loadAccountSettingsFormValues = createAsyncAction(
  createLoadingAction(() => async (dispatch, getState) => {
    const values = getFormValues(CUSTOMER_SETTINGS_SEARCH_FORM)(getState());
    const accountSettings = await ignoreCatch(
      dispatch(
        fetchAccountSettings(values[ACCOUNT_FIELD], values[BUSINESS_ID_FIELD])
      )
    );

    if (accountSettings) {
      dispatch(
        change(
          CUSTOMER_SETTINGS,
          SPECIAL_SERVICE_EXCLUSIONS_FIELD,
          accountSettings?.specialServices || []
        )
      );
    }
  }),
  ACTIONS.LOAD_ACCOUNT_SETTINGS
);

export const onSaveAccountSettings = createAsyncAction(
  createLoadingAction(() => async (dispatch, getState) => {
    const state = getState();
    const searchFormValues = getFormValues(CUSTOMER_SETTINGS_SEARCH_FORM)(
      state
    );
    const customerSettings = getFormValues(CUSTOMER_SETTINGS)(state);
    const customerAccountSettings = customerCustomerSettingsFromForms(
      searchFormValues,
      customerSettings
    );

    await customerSettingsService.updateCustomerSettings(
      customerAccountSettings
    );

    dispatch(showInfoModal(ACCOUNT_SETTINGS_UPDATED_SUCCESSFULLY));
  }),
  ACTIONS.SAVE_CUSTOMER_SETTINGS
);

const fetchChatQueue = createAsyncAction(
  customerSettingsService.getChatQueue,
  ACTIONS.FETCH_CHAT_QUEUE
);

export const loadChatQueueFormValues = createAsyncAction(
  createLoadingAction(() => async (dispatch, getState) => {
    const values = getFormValues(CUSTOMER_SETTINGS_SEARCH_FORM)(getState());
    const chatQueue = await ignoreCatch(
      dispatch(fetchChatQueue(values[ACCOUNT_FIELD], values[BUSINESS_ID_FIELD]))
    );

    dispatch(
      change(
        CUSTOMER_SETTINGS_CHAT_QUEUE_FORM,
        QUEUE_TYPE_FIELD,
        chatQueue?.queueType || ""
      )
    );
  }),
  ACTIONS.LOAD_CHAT_QUEUE
);

const saveChatQueueType = (queueType) => async (dispatch, getState) => {
  const values = getFormValues(CUSTOMER_SETTINGS_SEARCH_FORM)(getState());

  try {
    const result = await customerSettingsService.updateChatQueue({
      account: values[ACCOUNT_FIELD],
      businessId: String(values[BUSINESS_ID_FIELD]),
      queueType: queueType || "",
    });

    dispatch(
      change(
        CUSTOMER_SETTINGS_CHAT_QUEUE_FORM,
        QUEUE_TYPE_FIELD,
        result?.queueType || ""
      )
    );
    dispatch(
      showInfoModal(
        queueType
          ? CHAT_QUEUE_UPDATED_SUCCESSFULLY
          : CHAT_QUEUE_REMOVED_SUCCESSFULLY
      )
    );
    dispatch(refreshChatQueueAccounts());
    return result;
  } catch (err) {
    dispatch(showInfoModal(CHAT_QUEUE_UPDATE_ERROR));
    return null;
  }
};

export const onSaveChatQueue = createAsyncAction(
  createLoadingAction(() => async (dispatch, getState) => {
    const chatQueueForm = getFormValues(CUSTOMER_SETTINGS_CHAT_QUEUE_FORM)(
      getState()
    );
    await dispatch(saveChatQueueType(chatQueueForm?.[QUEUE_TYPE_FIELD] || ""));
  }),
  ACTIONS.SAVE_CHAT_QUEUE
);

const getBulkChatQueueGroupsFromForm = (getState) => {
  const bulkFormValues = getFormValues(CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM)(
    getState()
  );

  return [
    {
      accounts: parseBulkAccountNumbers(bulkFormValues?.[BULK_ACCOUNTS_FIELD]),
      queueType: bulkFormValues?.[QUEUE_TYPE_FIELD],
    },
  ];
};

const onBulkSaveChatQueue = createAsyncAction(
  createLoadingAction((groups) => async (dispatch, getState) => {
    const bulkGroups = (
      groups && groups.length
        ? groups
        : getBulkChatQueueGroupsFromForm(getState)
    )
      .map((group) => ({
        accounts: parseBulkAccountNumbers(group.accounts),
        queueType: group.queueType,
      }))
      .filter((group) => group.accounts.length && group.queueType);

    if (!bulkGroups.length) {
      dispatch(showInfoModal(BULK_CHAT_QUEUE_ERROR));
      return;
    }

    try {
      const succeeded = [];
      const failed = [];
      const results = await Promise.all(
        bulkGroups.map((group) =>
          customerSettingsService.bulkUpdateChatQueue(group)
        )
      );

      results.forEach((result) => {
        succeeded.push(...(result?.succeeded || []));
        failed.push(...(result?.failed || []));
      });

      const succeededCount = succeeded.length;
      const failedCount = failed.length;
      const failedAccounts = failed
        .map((item) =>
          item.reason ? `${item.account} (${item.reason})` : item.account
        )
        .join(", ");

      dispatch(
        showInfoModal(
          failedCount
            ? `${BULK_CHAT_QUEUE_APPLIED}. ${succeededCount} updated, ${failedCount} failed${
                failedAccounts ? `: ${failedAccounts}` : ""
              }.`
            : `${BULK_CHAT_QUEUE_APPLIED}. ${succeededCount} account(s) updated.`
        )
      );

      if (!failedCount) {
        dispatch(reset(CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM));
      }

      if (succeededCount) {
        dispatch(refreshChatQueueAccounts());
      }

      return { succeeded, failed };
    } catch (err) {
      dispatch(showInfoModal(BULK_CHAT_QUEUE_ERROR));
      return null;
    }
  }),
  ACTIONS.BULK_SAVE_CHAT_QUEUE
);

const confirmBulkChatQueueSave = (groups, contentText) => (dispatch) => {
  dispatch(
    showModal({
      contentText,
      cancelButtonText: NO,
      confirmButtonText: YES,
      onConfirmClick: () => dispatch(onBulkSaveChatQueue(groups)),
    })
  );
};

export const onConfirmBulkSaveChatQueue = () => (dispatch, getState) => {
  const groups = getBulkChatQueueGroupsFromForm(getState);
  const [{ accounts = [], queueType } = {}] = groups;

  dispatch(
    confirmBulkChatQueueSave(
      groups,
      formatMessage(ARE_YOU_SURE_BULK_CHAT_QUEUE, queueType, accounts.length)
    )
  );
};

export const onUploadBulkChatQueueCsv =
  (file) => async (dispatch, getState) => {
    if (!isCsvFile(file)) {
      dispatch(showInfoModal(BULK_CHAT_QUEUE_CSV_INVALID_FILE));
      return;
    }

    let csvText = "";

    try {
      csvText = await file.text();
    } catch (err) {
      dispatch(showInfoModal(BULK_CHAT_QUEUE_CSV_READ_ERROR));
      return;
    }

    const parsedCsv = parseBulkChatQueueCsv(csvText);

    if (!parsedCsv.accounts.length) {
      dispatch(showInfoModal(BULK_CHAT_QUEUE_CSV_NO_ACCOUNTS));
      return;
    }

    if (parsedCsv.accounts.length > MAX_BULK_CHAT_QUEUE_ACCOUNTS) {
      dispatch(showInfoModal(CHAT_QUEUE_FORM_ERRORS.TOO_MANY_ACCOUNTS));
      return;
    }

    dispatch(
      change(
        CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM,
        BULK_ACCOUNTS_FIELD,
        parsedCsv.accounts
      )
    );

    if (parsedCsv.queueType) {
      dispatch(
        change(
          CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM,
          QUEUE_TYPE_FIELD,
          parsedCsv.queueType
        )
      );
    }

    const bulkFormValues = getFormValues(
      CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM
    )(getState());
    const fallbackQueueType = bulkFormValues?.[QUEUE_TYPE_FIELD];
    const groups = groupBulkChatQueueItems(parsedCsv.items, fallbackQueueType);

    if (!groups.length) {
      dispatch(showInfoModal(CHAT_QUEUE_FORM_ERRORS.MISSING_QUEUE_TYPE));
      return;
    }

    const accountCount = groups.reduce(
      (total, group) => total + group.accounts.length,
      0
    );
    const contentText =
      groups.length > 1
        ? formatMessage(ARE_YOU_SURE_BULK_CHAT_QUEUE_CSV, accountCount)
        : formatMessage(
            ARE_YOU_SURE_BULK_CHAT_QUEUE,
            groups[0].queueType,
            accountCount
          );

    dispatch(confirmBulkChatQueueSave(groups, contentText));
  };

export const fetchChatQueueAccounts = createAsyncAction(
  customerSettingsService.getChatQueueAccounts,
  ACTIONS.FETCH_CHAT_QUEUE_ACCOUNTS
);
